import { useCallback, useState } from 'react';
import classNames from 'classnames';

import { useTranslator } from '@picsart/rc/services/localization';
import {
  Button,
  ButtonElementTypes,
  ButtonVariants,
} from '@picsart/design-system/components/Button';

import {
  ColorOptions,
  mapColorOptionToCascadeColor,
} from 'redesignedComponents/atoms/Overlay/enums';
import { getButtonOption } from 'redesignedComponents/organisms/CTA/utils';
import AuthModal from 'components/AuthModal';
import Description from '@picsart/growth-rc/Description';
import addAnalyticsParamsToUrl from 'utils/addAnalyticsParamsToUrl';
import Image from 'components/Image';
import useSubscriptionStore from 'stores/useSubscriptionStore';
import { useAuthContext } from '@picsart/growth-rc/AuthContext';
import { PricingCardProps } from './types';
import useStyles from './styles';

export default function PricingCard({
  currencySymbol = '$',
  monthlyPrice,
  cardButtonLabel,
  cardDescription,
  cardButtonProps,
  className,
  cardTitle,
  cardColor,
  cardNote,
  cardName,
  cardLogo,
  cardHref,
  cardId,
  comingSoon,
  pricingCardWidth,
  pricingCardHeight,
  ...rest
}: PricingCardProps) {
  const translate = useTranslator();
  const parsedColor = cardId ? cardColor : mapColorOptionToCascadeColor[ColorOptions[cardColor]];
  const buttonSkin = cardId ? cardButtonProps : getButtonOption(cardButtonProps?.skin);
  const { setRedirectTo } = useSubscriptionStore(store => ({
    setRedirectTo: store.setRedirectTo,
  }));
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { user } = useAuthContext();

  const classes = useStyles({ theme: { color: parsedColor }, pricingCardWidth, pricingCardHeight });

  const openRegModal = useCallback(() => {
    if (!user) {
      setShowAuthModal(prevState => !prevState);
    } else {
      const url = addAnalyticsParamsToUrl(cardHref, {
        source: 'pricing_card',
        origin: 'pricing_card',
      });
      setRedirectTo(url);
    }
  }, [cardHref, setRedirectTo, user]);

  const goToNextUrl = useCallback(() => {
    if (cardHref) {
      const url = addAnalyticsParamsToUrl(cardHref, {
        source: 'pricing_card',
        origin: 'pricing_card',
      });
      setRedirectTo(url);
    }
  }, [cardHref, setRedirectTo]);

  return (
    <div className={classNames(classes.root, className)} {...rest}>
      {!!cardName && <strong className={classes.name}>{translate(cardName)}</strong>}
      {!!cardLogo && <Image className={classes.logo} src={cardLogo} alt={cardId} />}
      <div className={classes.price}>
        {monthlyPrice === 'Custom' ? (
          <span>{monthlyPrice}</span>
        ) : (
          <>
            <span>
              {currencySymbol}
              {monthlyPrice}
            </span>
            <small>{translate('/mo.')}</small>
          </>
        )}
      </div>
      <Button
        {...cardButtonProps}
        variant={ButtonVariants[cardButtonProps?.variant]}
        skin={buttonSkin?.skin}
        data-pulse-name={cardButtonLabel}
        tag={ButtonElementTypes.Button}
        target="_blank"
        onClick={openRegModal}
        isFullWidth
      >
        {translate(cardButtonLabel)}
      </Button>
      <div className={classes.note}>{cardNote ? translate(cardNote) : ''}</div>
      <div className={classes.separator} />
      <strong className={classes.title}>{translate(cardTitle)}</strong>
      {typeof cardDescription === 'string' ? (
        <p className={classes.description}>{translate(cardDescription)}</p>
      ) : (
        <Description {...cardDescription} className={classes.cmsDescription} />
      )}
      {showAuthModal !== null && <AuthModal isOpen={showAuthModal} successHandler={goToNextUrl} />}
    </div>
  );
}
